import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Download, PrimaryButton, TertiaryButton, ZazumeModal } from '@zazume/zzm-base';
import { useGet } from '../../hooks/api/useGet';
import { API } from '../../lib/network/API';
import { FC } from 'react';
import { isImage } from '../../utils/fileHelper';
import { useI18n } from '../../hooks/useI18n';
import { Document } from '../../models';
import { PdfViewer } from './PdfViewer';
import { DocumentModalInfo } from './documentModalInfo/DocumentModalInfo';
import { useSigningDocument } from '../../hooks/application/useSigningDocument';
import { RejectSignatureButton } from './atoms/RejectSignatureButton';
import { InvoiceDocumentModal } from './InvoiceDocumentModal';
import { useDownloadDocument } from '#/hooks/documents/useDownloadDocument';
import { DocumentWithControlsContainer } from './DocumentWithControlsContainer';
import { DesktopNavigationButtons } from './DesktopNavigationButtons';

const Footer = styled.div(({ theme }) => css`
  margin: ${theme.spacing.s10} ${theme.spacing.s0};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${theme.spacing.s8};
`);

const Container = styled.div(({ theme }) => css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.s10};

  ${theme.mq.tablet} {
    flex-direction: row;
  }
`);

const Image = styled.img(({ theme }) => css`
  max-width: 100%;
  object-fit: contain;

  ${theme.mq.tablet} {
    width: 31.25rem;
  }
`);

interface DocumentModalProps {
  document: Document;
  close: () => void;
  onAfterUpdate?: () => Promise<void>;
  canBeRejected?: boolean;
  onNextClick?: (id: string) => void;
  onPreviousClick?: (id: string) => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
}

// TODO: create DocumentModalContext to avoid passing props between multiple components

export const DocumentModal: FC<DocumentModalProps> = ({ close, document, canBeRejected = true, onAfterUpdate, onNextClick, onPreviousClick, hasNext, hasPrevious }) => {
  const { t } = useI18n();
  const { download } = useDownloadDocument(document._id);
  const { _id: documentId, filename } = document;
  const { rejectSigningDocument, sendSignatureReminder } = useSigningDocument(documentId);
  const { data, isLoading: isLoadingData } = useGet(['document_url', documentId], API.documents.getURL(documentId), { refetchOnWindowFocus: false });
  if (isLoadingData || !data) {
    return null;
  }

  const isDocumentImage = isImage(filename);
  const isInvoice = document.type === 'invoice' || document.type === 'creditNoteInvoice';

  if (isInvoice) {
    return <InvoiceDocumentModal
      document={document}
      close={close}
      onNextClick={onNextClick}
      onPreviousClick={onPreviousClick}
      hasNext={hasNext}
      hasPrevious={hasPrevious}
    />;
  }

  return (
    <DesktopNavigationButtons
      documentId={document._id}
      onNextClick={onNextClick}
      onPreviousClick={onPreviousClick}
      hasNext={hasNext}
      hasPrevious={hasPrevious}
    >
      <ZazumeModal title={filename} onClose={close}>
        <Container>
          <DocumentWithControlsContainer documentId={document._id} onNextClick={onNextClick} onPreviousClick={onPreviousClick} hasNext={hasNext} hasPrevious={hasPrevious}>
            {
              isDocumentImage
                ? <Image src={data.url} alt={filename}/>
                : <PdfViewer document={data}/>
            }
          </DocumentWithControlsContainer>
          <DocumentModalInfo document={document} onSendReminder={sendSignatureReminder} onAfterUpdate={onAfterUpdate}/>
        </Container>
        <Footer>
          <RejectSignatureButton document={document} canBeRejected={canBeRejected} onRejectDocument={rejectSigningDocument} onAfterReject={onAfterUpdate}/>
          <TertiaryButton onClick={close}>
            {t('general.close')}
          </TertiaryButton>
          <PrimaryButton onClick={download}>
            <Download/> {t('documentsModals.downloadDocument')}
          </PrimaryButton>
        </Footer>
      </ZazumeModal>
    </DesktopNavigationButtons>
  );
};
